import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import useAxiosFetch from "../common/useAxiosFetch";
import { useAppContext } from "../AppContext";

// Loads the organizations and domains (projects) the user can switch between and
// keeps the selected organization/domain in sync with localStorage.
const useScope = () => {
  const { t } = useTranslation();
  const { currentUser, currentOrg, setCurrentOrg, currentProject, setCurrentProject, headerRefresh } = useAppContext();

  const { data: organizations, loading: orgsLoading, refetch: refetchOrgs } = useAxiosFetch("/api/organizations");
  const { data: projects, loading: projectsLoading, refetch: refetchProjects } = useAxiosFetch(`/api/projects/byOrg/${currentOrg.id}`);

  useEffect(() => {
    refetchOrgs();
    refetchProjects();
  }, [headerRefresh]);

  const selectProject = (projectId, projectName) => {
    const project = { id: projectId, name: projectName };
    currentUser.projectId = projectId;
    localStorage.setItem("currentProject", JSON.stringify(project));
    setCurrentProject(project);
  };

  const selectOrg = (org) => {
    if (org.id === currentOrg.id) return;
    const nextOrg = {
      id: org.id,
      name: org.name,
      securityStandard: org.securityStandard || "ISO27001",
      rangeBounds: org.rangeBounds,
    };
    currentUser.orgId = org.id;
    localStorage.setItem("currentOrg", JSON.stringify(nextOrg));
    setCurrentOrg(nextOrg);
    // A domain never belongs to two organizations, so start from all domains of the new one.
    selectProject("", t("all_domains"));
  };

  // "All domains" is meaningless with a single domain, so select that domain directly.
  useEffect(() => {
    if (projectsLoading || currentProject.id || projects?.length !== 1) return;
    // useAxiosFetch keeps the previous organization's data until the new request resolves.
    if (projects[0].orgId && projects[0].orgId !== currentOrg.id) return;
    selectProject(projects[0].id, projects[0].name);
  }, [projectsLoading, projects, currentProject.id, currentOrg.id]);

  // Fall back to a valid domain when the selected one no longer exists in this organization.
  useEffect(() => {
    if (orgsLoading || projectsLoading || !currentProject.id) return;
    const found = projects?.find((project) => project.id === currentProject.id);
    if (!found && projects?.length > 0) {
      currentUser.projectId = projects[0].id;
      setCurrentProject({ id: projects[0].id, name: projects[0].name });
    } else if (!projects?.length) {
      currentUser.projectId = null;
      setCurrentProject({ id: null, name: "No domains" });
    }
  }, [orgsLoading, projectsLoading, currentProject, projects, currentUser, setCurrentProject]);

  const orgName = organizations?.find((org) => org.id === currentOrg.id)?.name || currentOrg.name;
  const projectName = projects?.find((project) => project.id === currentProject.id)?.name || (currentProject.id ? currentProject.name : t("all_domains"));

  return {
    organizations: organizations || [],
    projects: projects || [],
    loading: orgsLoading || projectsLoading,
    orgName,
    projectName,
    selectOrg,
    selectProject,
  };
};

export default useScope;
